if ( typeof PromoPopup !== 'function' ) {

  class PromoPopup extends HTMLElement {

    connectedCallback() {

      this.card = this.querySelector('[data-js-promo-card]');
      this.tab = this.querySelector('[data-js-promo-tab]');
      this.link = this.card.querySelector('a');
      this.delay = parseFloat(this.dataset.delay || 1) * 1000;
      this.duration = parseFloat(this.dataset.duration || 5) * 1000;
      this.storageKey = `promo-popup-${this.dataset.sectionId}-seen`;

      this.tab.addEventListener('click', ()=>{
        this.open();
        this.link.focus({ preventScroll: true });
      });
      this.querySelector('[data-js-promo-close]').addEventListener('click', ()=>this.close());
      this.addEventListener('keydown', e=>{
        if ( e.key === 'Escape' && this.card.classList.contains('is-visible') ) {
          this.close();
        }
      });

      // don't pop down while the visitor is hovering or tabbing through it
      this.card.addEventListener('mouseleave', ()=>this.resumeAutoClose());
      this.card.addEventListener('focusout', e=>{
        if ( ! this.card.contains(e.relatedTarget) ) {
          this.resumeAutoClose();
        }
      });

      this.avoidCornerWidgets();

      if ( window.Shopify && Shopify.designMode ) {
        document.addEventListener('shopify:section:select', e=>{
          if ( e.detail.sectionId === this.dataset.sectionId ) this.open();
        });
        document.addEventListener('shopify:section:deselect', e=>{
          if ( e.detail.sectionId === this.dataset.sectionId ) this.close();
        });
      }

      if ( this.dataset.frequency === 'session' && this.storage('get') ) {
        this.showTab();
      } else {
        this.whenPageVisible(()=>{
          setTimeout(()=>{
            if ( this.card.classList.contains('is-visible') ) return;
            this.open(this.duration);
            this.storage('set');
          }, this.delay);
        });
      }

    }

    open(autoCloseAfter = 0) {
      clearTimeout(this.autoCloseTimer);
      this.pendingAutoClose = false;

      this.card.removeAttribute('inert');
      this.card.classList.add('is-visible');
      this.tab.setAttribute('inert', '');
      this.tab.setAttribute('aria-expanded', 'true');
      this.tab.classList.remove('is-visible');

      if ( autoCloseAfter ) {
        this.autoCloseTimer = setTimeout(()=>this.autoClose(), autoCloseAfter);
      }
    }

    close() {
      clearTimeout(this.autoCloseTimer);
      this.pendingAutoClose = false;

      const hadFocus = this.card.contains(document.activeElement);
      this.card.setAttribute('inert', '');
      this.card.classList.remove('is-visible');
      this.showTab();
      if ( hadFocus ) {
        this.tab.focus({ preventScroll: true });
      }
    }

    autoClose() {
      if ( this.card.matches(':hover') || this.card.contains(document.activeElement) ) {
        this.pendingAutoClose = true;
      } else {
        this.close();
      }
    }

    resumeAutoClose() {
      if ( this.pendingAutoClose ) {
        this.pendingAutoClose = false;
        this.autoCloseTimer = setTimeout(()=>this.autoClose(), 1000);
      }
    }

    showTab() {
      this.tab.removeAttribute('inert');
      this.tab.setAttribute('aria-expanded', 'false');
      this.tab.classList.add('is-visible');
    }

    whenPageVisible(callback) {
      if ( document.visibilityState === 'visible' ) {
        callback();
        return;
      }
      const onChange = ()=>{
        if ( document.visibilityState === 'visible' ) {
          document.removeEventListener('visibilitychange', onChange);
          callback();
        }
      };
      document.addEventListener('visibilitychange', onChange);
    }

    // keep clear of other widgets pinned to the bottom of the screen:
    // the theme's cookie / newsletter popups, Shopify's hCaptcha badge and the Inbox "Contact" button
    avoidCornerWidgets() {
      const fullWidth = window.matchMedia('(max-width: 575px)'); // same breakpoint as the css

      const update = ()=>{
        const viewportWidth = document.documentElement.clientWidth;
        const edge = parseFloat(getComputedStyle(this).getPropertyValue('--promo-popup-gap'));
        const boxLeft = fullWidth.matches ? edge : viewportWidth - edge - this.offsetWidth;
        const boxRight = viewportWidth - edge;

        // on desktop there's room to sit next to the badge, on phones the popup goes above it
        const overlapping = this.cornerWidgets()
          .map(({ widget, sideways })=>({ rect: widget.getBoundingClientRect(), sideways: sideways && ! fullWidth.matches }))
          .filter(({ rect })=>rect.height && rect.right > boxLeft && rect.left < boxRight);

        let shiftUp = 0;
        overlapping.filter(widget=>! widget.sideways).forEach(({ rect })=>{
          shiftUp = Math.max(shiftUp, window.innerHeight - rect.top);
        });

        const popupBottom = window.innerHeight - (shiftUp ? shiftUp + 12 : edge);
        let shiftLeft = 0;
        overlapping.filter(widget=>widget.sideways && widget.rect.top < popupBottom).forEach(({ rect })=>{
          shiftLeft = Math.max(shiftLeft, viewportWidth - rect.left);
        });

        this.setShift('--promo-popup-offset', shiftUp);
        this.setShift('--promo-popup-right', shiftLeft);
      };

      // widgets animate in over ~350ms, measure once they've settled
      const scheduleUpdate = ()=>{
        if ( this.updateTimer ) return;
        this.updateTimer = setTimeout(()=>{
          this.updateTimer = null;
          this.observeChat(observer);
          update();
        }, 400);
      };
      const observer = new MutationObserver(scheduleUpdate);
      document.querySelectorAll('.popup-block').forEach(popup=>{
        observer.observe(popup, { attributes: true, attributeFilter: ['class'] });
        popup.addEventListener('transitionend', scheduleUpdate);
      });
      observer.observe(document.body, { childList: true }); // the hCaptcha badge and the chat are injected late
      window.addEventListener('resize', scheduleUpdate);
      this.observeChat(observer);
      update();
    }

    cornerWidgets() {
      const widgets = [...document.querySelectorAll('.popup-block.active')].map(widget=>({ widget }));
      const badge = document.getElementById('shop-hcaptcha-badge-container');
      if ( badge ) {
        widgets.push({ widget: badge, sideways: true });
      }
      const chatButton = this.chatRoot && this.chatRoot.querySelector('.activator-nudge');
      if ( chatButton ) {
        widgets.push({ widget: chatButton });
      }
      return widgets;
    }

    // the Shopify Inbox button renders inside the chat's shadow root after the app loads
    observeChat(observer) {
      const chat = document.querySelector('shopify-chat');
      if ( this.chatRoot || ! chat || ! chat.shadowRoot ) return;
      this.chatRoot = chat.shadowRoot;
      observer.observe(this.chatRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });
    }

    setShift(property, distance) {
      if ( distance ) {
        this.style.setProperty(property, `${Math.ceil(distance) + 12}px`);
      } else {
        this.style.removeProperty(property);
      }
    }

    storage(action) {
      try {
        if ( action === 'set' ) {
          sessionStorage.setItem(this.storageKey, '1');
        }
        return sessionStorage.getItem(this.storageKey) === '1';
      } catch (e) {
        return false;
      }
    }

  }

  if ( typeof customElements.get('promo-popup') == 'undefined' ) {
    customElements.define('promo-popup', PromoPopup);
  }

}
